// The assistant's entry point in the header (spec 2026-10-21-24 §12): under
// an `$org` route only, for a staff member of an organization with the
// `assistant` flag. Outside the desktop-only group, so phones get it.
import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiRobotOutline } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useFeatureFlag } from '../../hooks/use-feature-flags'
import { useMemberKind } from '../../contexts/PermissionsContext'
import { AssistantPanel, readStored, writeStored } from './AssistantPanel'

/** showAssistant: an organization, the flag on, a staff member. */
export function showAssistant(org: string | undefined, flag: boolean, kind: string | undefined): boolean {
    return !!org && flag === true && kind === 'staff'
}

function AssistantToggle({ org }: { org: string }) {
    const { t } = useTranslation('assistant')
    const flag = useFeatureFlag<boolean>(org, 'assistant', false)
    const kind = useMemberKind()
    const [open, setOpenState] = useState(() => readStored(org, 'open') === '1')
    if (!showAssistant(org, flag, kind)) return null
    const setOpen = (v: boolean) => {
        setOpenState(v)
        writeStored(org, 'open', v ? '1' : null)
    }
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="p-2 hover:bg-accent rounded-md transition-colors"
                aria-label={t('toggle')}
                title={t('toggle')}
                data-testid="assistant-toggle"
            >
                <AppIcon path={mdiRobotOutline} size="sm" />
            </button>
            <AssistantPanel org={org} open={open} onOpenChange={setOpen} />
        </>
    )
}

export function AssistantGate() {
    const { org } = useParams({ strict: false }) as { org?: string }
    if (!org) return null
    return <AssistantToggle key={org} org={org} />
}
